/**
 * Project Service
 *
 * Handles all project-related API calls.
 */

import api from './api';
import type {
  ApiResponse,
  Project,
  ProjectWithStats,
  ProjectCostDataPoint,
  CreateProjectRequest,
  UpdateProjectRequest,
} from '@budgira/shared';

export const projectService = {
  /**
   * Get all projects for the authenticated user
   * @param includeArchived - Whether to include archived projects
   */
  async list(includeArchived: boolean = false): Promise<ProjectWithStats[]> {
    const params = includeArchived ? '?includeArchived=true' : '';
    const response = await api.get<ApiResponse<ProjectWithStats[]>>(`/projects${params}`);
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error?.message || 'Failed to fetch projects');
    }
    return response.data.data;
  },

  /**
   * Get a specific project by ID
   */
  async getById(id: string): Promise<ProjectWithStats> {
    const response = await api.get<ApiResponse<ProjectWithStats>>(`/projects/${id}`);
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error?.message || 'Failed to fetch project');
    }
    return response.data.data;
  },

  /**
   * Get cost evolution data for a project (for charts)
   */
  async getCostEvolution(id: string): Promise<ProjectCostDataPoint[]> {
    const response = await api.get<ApiResponse<ProjectCostDataPoint[]>>(
      `/projects/${id}/cost-evolution`
    );
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error?.message || 'Failed to fetch cost evolution');
    }
    return response.data.data;
  },

  /**
   * Create a new project
   */
  async create(data: CreateProjectRequest): Promise<Project> {
    const response = await api.post<ApiResponse<Project>>('/projects', data);
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error?.message || 'Failed to create project');
    }
    return response.data.data;
  },

  /**
   * Update an existing project
   */
  async update(id: string, data: UpdateProjectRequest): Promise<Project> {
    const response = await api.put<ApiResponse<Project>>(`/projects/${id}`, data);
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error?.message || 'Failed to update project');
    }
    return response.data.data;
  },

  /**
   * Delete a project
   */
  async delete(id: string): Promise<void> {
    const response = await api.delete<ApiResponse<null>>(`/projects/${id}`);
    if (!response.data.success) {
      throw new Error(response.data.error?.message || 'Failed to delete project');
    }
  },

  /**
   * Archive a project
   */
  async archive(id: string): Promise<Project> {
    const response = await api.post<ApiResponse<Project>>(`/projects/${id}/archive`);
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error?.message || 'Failed to archive project');
    }
    return response.data.data;
  },

  /**
   * Mark a project as completed
   */
  async complete(id: string): Promise<Project> {
    const response = await api.post<ApiResponse<Project>>(`/projects/${id}/complete`);
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error?.message || 'Failed to complete project');
    }
    return response.data.data;
  },

  /**
   * Reactivate a project
   */
  async reactivate(id: string): Promise<Project> {
    const response = await api.post<ApiResponse<Project>>(`/projects/${id}/reactivate`);
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error?.message || 'Failed to reactivate project');
    }
    return response.data.data;
  },
};

export default projectService;
